import { useState, useEffect } from "react";
import { allAgents } from "@/mocks/leadsData";
import { fetchApi } from "@/services/apiService";

type ActivityFormData = {
  type: string;
  description: string;
  date: string;
  time: string;
  agent: string;
};

type Props = {
  onClose: () => void;
  onSave: (data: ActivityFormData) => void;
};

const activityTypes = ["Call", "Note", "Email", "WhatsApp", "Meeting", "Stage Change", "Other"];

export function AddActivityModal({ onClose, onSave }: Props) {
  const [type, setType] = useState("Call");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [time, setTime] = useState(
    new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
  const [agent, setAgent] = useState("Rajesh Kumar");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dbAgents, setDbAgents] = useState<string[]>([]);

  useEffect(() => {
    fetchApi<any>('/auth/company-user/all', undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const agentNames: string[] = [];
        userList.forEach((t: any) => {
          if (t.name) agentNames.push(t.name);
          else if (t.fullName) agentNames.push(t.fullName);
          else if (t.username) agentNames.push(t.username);
          if (t.leader_name) agentNames.push(t.leader_name);
          if (Array.isArray(t.member_names)) agentNames.push(...t.member_names);
        });
        const unique = Array.from(new Set(agentNames)).filter(Boolean);
        if (unique.length > 0) {
          setDbAgents(unique);
          setAgent(unique[0]);
        }
      })
      .catch(() => {});
  }, []);

  const agentsList = dbAgents.length > 0 ? dbAgents : allAgents;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!description.trim()) e.description = "Description is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({ type, description: description.trim(), date, time, agent });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Add Activity</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Activity Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Activity Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
            >
              {activityTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              placeholder="Describe the activity..."
              rows={4}
              className={`w-full border rounded-lg px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all ${
                errors.description ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"
              }`}
            />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
              />
            </div>
          </div>

          {/* Logged By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Logged By</label>
            <select
              value={agent}
              onChange={(e) => setAgent(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
            >
              {agentsList.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap cursor-pointer transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg whitespace-nowrap cursor-pointer transition-colors"
          >
            Add Activity
          </button>
        </div>
      </div>
    </div>
  );
}